import { useEffect, useState } from "react";
import { MapPin, FloppyDisk, Crosshair, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import FixIncidenceTz from "../components/FixIncidenceTz";

const DEFAULT_RADIUS = 50;

export default function AdminSettings() {
  const [form, setForm] = useState({
    geofence_enabled: false,
    workplace_lat: "",
    workplace_lng: "",
    workplace_radius_m: DEFAULT_RADIUS,
    invoice_upload_pin: "",
    hide_edit_delete_punch: false,
    break_discount_time: false,
    break_reasons: [],
    break_families: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setForm({
          geofence_enabled: !!data.geofence_enabled,
          workplace_lat: data.workplace_lat ?? "",
          workplace_lng: data.workplace_lng ?? "",
          workplace_radius_m: data.workplace_radius_m ?? DEFAULT_RADIUS,
          invoice_upload_pin: data.invoice_upload_pin ?? "",
          hide_edit_delete_punch: !!data.hide_edit_delete_punch,
          break_discount_time: !!data.break_discount_time,
          break_reasons: Array.isArray(data.break_reasons)
            ? data.break_reasons.map((r) => typeof r === "string"
                ? { name: r, discounts: false, family_id: (data.break_families?.[0]?.id) || "", visible_roles: ["todos"], requires_pin: true }
                : { name: r.name || "", discounts: !!r.discounts, family_id: r.family_id || "", visible_roles: r.visible_roles || ["todos"], requires_pin: r.requires_pin !== false })
            : [],
          break_families: Array.isArray(data.break_families)
            ? data.break_families.map((f) => ({ id: f.id, name: f.name || "", color: f.color || "#94a3b8" }))
            : [],
        });
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          workplace_lat: pos.coords.latitude.toFixed(6),
          workplace_lng: pos.coords.longitude.toFixed(6),
        }));
        setLocating(false);
        toast.success("Ubicación capturada");
      },
      (err) => {
        setLocating(false);
        toast.error("No se pudo obtener la ubicación: " + err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        geofence_enabled: !!form.geofence_enabled,
        workplace_lat: form.workplace_lat === "" ? null : Number(form.workplace_lat),
        workplace_lng: form.workplace_lng === "" ? null : Number(form.workplace_lng),
        workplace_radius_m: Number(form.workplace_radius_m) || DEFAULT_RADIUS,
        invoice_upload_pin: (form.invoice_upload_pin || "").trim(),
        hide_edit_delete_punch: !!form.hide_edit_delete_punch,
        // Preserve tasks config (edited in /admin/tasks) — PUT /settings requires it.
        break_discount_time: !!form.break_discount_time,
        break_families: (form.break_families || [])
          .map((f) => ({ id: f.id || undefined, name: (f?.name || "").trim(), color: (f?.color || "#94a3b8").trim() }))
          .filter((f) => f.name),
        break_reasons: (form.break_reasons || [])
          .map((r) => ({ name: (r?.name || "").trim(), discounts: !!r?.discounts, family_id: r?.family_id || "", visible_roles: r?.visible_roles || ["todos"], requires_pin: r?.requires_pin !== false }))
          .filter((r) => r.name),
      };
      await api.put("/settings", payload);
      toast.success("Ajustes guardados");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[var(--text-secondary)]">Cargando…</div>;

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <div className="label-cap">configuración</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Ajustes del local</h1>
      </div>

      <form onSubmit={save} className="space-y-6">
        <div className="card-flat p-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.geofence_enabled}
              onChange={(e) => onChange("geofence_enabled", e.target.checked)}
              data-testid="geofence-toggle"
              className="w-5 h-5 mt-1 accent-[var(--primary)] cursor-pointer"
            />
            <div>
              <div className="font-display font-bold text-lg">Restringir fichaje por ubicación</div>
              <div className="text-sm text-[var(--text-secondary)]">
                Si está activado, los trabajadores solo podrán fichar entrada/salida si están físicamente
                dentro del radio configurado alrededor del local.
              </div>
            </div>
          </label>
        </div>

        <div className="card-flat p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <MapPin size={22} weight="duotone" color="#4A5D23" />
            <div className="font-display font-bold text-lg">Ubicación del local</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-cap block mb-2">Latitud</label>
              <input
                type="number"
                step="0.000001"
                value={form.workplace_lat}
                onChange={(e) => onChange("workplace_lat", e.target.value)}
                data-testid="workplace-lat"
                className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)] font-mono"
                placeholder="40.416775"
              />
            </div>
            <div>
              <label className="label-cap block mb-2">Longitud</label>
              <input
                type="number"
                step="0.000001"
                value={form.workplace_lng}
                onChange={(e) => onChange("workplace_lng", e.target.value)}
                data-testid="workplace-lng"
                className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)] font-mono"
                placeholder="-3.703790"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={useMyLocation}
            disabled={locating}
            data-testid="use-current-location"
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[var(--border)] hover:bg-[var(--bg)] font-display font-semibold disabled:opacity-50"
          >
            <Crosshair size={18} weight="duotone" />
            {locating ? "Obteniendo ubicación…" : "Usar mi ubicación actual"}
          </button>

          <div>
            <label className="label-cap block mb-2">Radio permitido (metros)</label>
            <input
              type="number"
              min="10"
              max="5000"
              step="10"
              value={form.workplace_radius_m}
              onChange={(e) => onChange("workplace_radius_m", e.target.value)}
              data-testid="workplace-radius"
              className="w-full md:w-48 px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)] font-mono"
            />
            <div className="text-xs text-[var(--text-secondary)] mt-1">
              Distancia máxima desde el punto del local en la que se permite fichar. Por defecto 50 m.
            </div>
          </div>

          {form.workplace_lat && form.workplace_lng && (
            <a
              href={`https://www.google.com/maps?q=${form.workplace_lat},${form.workplace_lng}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[var(--primary)] underline"
            >
              Ver en Google Maps →
            </a>
          )}
        </div>

        <div className="card-flat p-4 bg-[#FFF9E6] border-[#E6D88A] flex items-start gap-3">
          <Warning size={20} weight="duotone" color="#B48A00" />
          <div className="text-sm text-[var(--text-secondary)]">
            Cuando el geofence está activo, el navegador pedirá permiso de ubicación a cada trabajador al
            fichar. Si lo deniega o el dispositivo no tiene GPS preciso, no podrá fichar.
          </div>
        </div>

        <div className="card-flat p-6 space-y-3">
          <div className="font-display font-bold text-lg">Carga de facturas desde el fichaje</div>
          <div className="text-sm text-[var(--text-secondary)]">
            Define un PIN de 4 dígitos para que el personal pueda cargar facturas desde la pantalla principal sin entrar como admin.
            Déjalo vacío para desactivar esta función.
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{4}"
              maxLength={4}
              value={form.invoice_upload_pin || ""}
              onChange={(e) => onChange("invoice_upload_pin", e.target.value.replace(/\D/g, "").slice(0, 4))}
              data-testid="invoice-pin"
              placeholder="1234"
              className="w-40 px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)] font-mono text-2xl tracking-widest text-center"
            />
            <label
              className="inline-flex items-center gap-2 cursor-pointer select-none"
              data-testid="hide-punch-actions-toggle-label"
            >
              <input
                type="checkbox"
                checked={form.hide_edit_delete_punch}
                onChange={(e) => onChange("hide_edit_delete_punch", e.target.checked)}
                data-testid="hide-punch-actions-toggle"
                className="w-5 h-5 accent-[var(--primary)] cursor-pointer"
              />
              <span className="font-display font-semibold text-sm">
                {form.hide_edit_delete_punch ? "Activo" : "No activo"}
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-save"
            className="btn-primary px-6 py-3 rounded-xl font-display font-bold flex items-center gap-2 disabled:opacity-50"
          >
            <FloppyDisk size={18} weight="duotone" />
            {saving ? "Guardando…" : "Guardar ajustes"}
          </button>
        </div>
      </form>
      <div className="mt-6">
        <FixIncidenceTz />
      </div>
    </div>
  );
}
